/**
 * Fields the CMS fills in, and the small amount of UI that keeps that honest.
 *
 * Every one of these renders an ordinary editable input. The only thing added
 * is a line underneath saying where the value came from and, once an editor has
 * disagreed with it, a way back. A derived value that cannot be seen or changed
 * is worse than one an editor had to type, because the editor is still
 * accountable for it.
 */

import React, { useState } from "react";
import { FieldHelperText, FieldProps } from "@firecms/core";
import { Button, Chip, DebouncedTextField, Typography } from "@firecms/ui";

import type { DerivableKey } from "../derive/autofill";
import { deriveKeywords } from "../derive/text";
import { useDerivedState } from "./useDerivation";

/**
 * Lets a field be written from outside the input the editor is typing in.
 *
 * `DebouncedTextField` keeps its own copy of the value and, once it has seen a
 * keystroke, re-fires that last event whenever the prop disagrees with what it
 * holds. That is what makes it debounce, and it also means an external write
 * lands and is immediately overwritten by what the editor typed a moment ago:
 * pressing "use the derived value" appeared to do nothing at all. Remounting on
 * each external write clears that memory. The editor is clicking a button
 * rather than typing when this happens, so there is no caret to lose.
 */
function useExternalWrite<T>(setValue: (value: T | null) => void) {
    const [remountKey, setRemountKey] = useState(0);
    const write = (value: T | null) => {
        setValue(value);
        setRemountKey((key) => key + 1);
    };
    return { remountKey, write };
}

export interface DerivedFieldProps {
    /** Which derivation rule backs this field. */
    derivedKey: DerivableKey;
    multiline?: boolean;
    /** Character budget to show a count against, e.g. 60 for an SEO title. */
    softLimit?: number;
    /** Extra sentence shown under the field, before the derivation note. */
    note?: string;
}

/** "Auto from the title" / "You set this · Use the suggestion". */
function DerivationNote({
    state,
    onReset,
    disabled
}: {
    state: ReturnType<typeof useDerivedState>;
    onReset: () => void;
    disabled?: boolean;
}) {
    if (state.locked) {
        return (
            <Typography variant={"caption"} className={"opacity-70"}>
                {state.locked}
            </Typography>
        );
    }

    if (state.tracking) {
        return (
            <div className={"flex flex-row gap-2 items-center"}>
                <Chip size={"small"} colorScheme={"blueLighter"}>Auto</Chip>
                <Typography variant={"caption"} className={"opacity-70"}>
                    Filled in from {state.from}. Type over it and it stops updating.
                </Typography>
            </div>
        );
    }

    const derived = state.derived;
    return (
        <div className={"flex flex-row gap-2 items-center flex-wrap"}>
            <Chip size={"small"}>Yours</Chip>
            <Typography variant={"caption"} className={"opacity-70"}>
                No longer following {state.from}.
            </Typography>
            {derived !== undefined && !disabled && (
                <Button size={"small"} variant={"text"}
                        onClick={() => {
                            onReset();
                            /* Taking the suggestion is also a decision to let the
                               field follow again, otherwise the next title edit
                               would leave it stale. */
                            state.resume();
                        }}>
                    Use “{Array.isArray(derived) ? derived.join(", ") : String(derived)}”
                </Button>
            )}
        </div>
    );
}

export function DerivedTextField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    includeDescription,
    context,
    customProps
}: FieldProps<string, DerivedFieldProps>) {
    const state = useDerivedState(customProps.derivedKey, context);
    const { remountKey, write } = useExternalWrite<string>(setValue);
    const length = (value ?? "").length;
    const limit = customProps.softLimit;

    return (
        <div className={"flex flex-col gap-1"}>
            <DebouncedTextField
                key={remountKey}
                value={value ?? ""}
                label={property.name}
                multiline={customProps.multiline ?? false}
                minRows={customProps.multiline ? 2 : undefined}
                disabled={disabled ?? false}
                error={Boolean(showError && error)}
                onChange={(event) => setValue(event.target.value)}
                endAdornment={
                    limit ? (
                        <Typography
                            variant={"caption"}
                            className={length > limit ? "text-red-500" : "opacity-50"}>
                            {length}/{limit}
                        </Typography>
                    ) : undefined
                }
            />
            {customProps.note && (
                <Typography variant={"caption"} className={"opacity-70"}>
                    {customProps.note}
                </Typography>
            )}
            <DerivationNote
                state={state}
                disabled={disabled}
                onReset={() => write((state.derived as string) ?? "")}/>
            <FieldHelperText error={error}
                             showError={showError ?? false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
        </div>
    );
}

export function DerivedNumberField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    includeDescription,
    context,
    customProps
}: FieldProps<number, DerivedFieldProps>) {
    const state = useDerivedState(customProps.derivedKey, context);
    const { remountKey, write } = useExternalWrite<number>(setValue);

    return (
        <div className={"flex flex-col gap-1"}>
            <DebouncedTextField
                key={remountKey}
                type={"number"}
                value={value ?? ""}
                label={property.name}
                disabled={disabled ?? false}
                error={Boolean(showError && error)}
                onChange={(event) => {
                    const parsed = Number(event.target.value);
                    setValue(event.target.value === "" || Number.isNaN(parsed) ? null : parsed);
                }}
                endAdornment={<Typography variant={"caption"} className={"opacity-50"}>min</Typography>}/>
            <DerivationNote
                state={state}
                disabled={disabled}
                onReset={() => write((state.derived as number) ?? null)}/>
            <FieldHelperText error={error}
                             showError={showError ?? false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
        </div>
    );
}

/* How many alternatives to offer next to the keyword fields. Enough to pick
   from, few enough to read. */
const KEYWORD_ALTERNATIVES = 6;

/**
 * The primary keyword, with the runners-up one click away.
 *
 * The suggestions are counted word frequencies, not search data, and the field
 * says exactly that. Anything else would be the CMS inventing SEO on an
 * editor's behalf and letting them publish it without ever reading it.
 */
export function PrimaryKeywordField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    includeDescription,
    context
}: FieldProps<string, DerivedFieldProps>) {
    const state = useDerivedState("primaryKeyword", context);
    const { remountKey, write } = useExternalWrite<string>(setValue);
    const values = context.values as { title?: string; body?: string };
    const { primary, secondary } = deriveKeywords(
        values.title ?? "",
        values.body ?? "",
        KEYWORD_ALTERNATIVES
    );
    const alternatives = [primary, ...secondary].filter(
        (candidate): candidate is string => Boolean(candidate) && candidate !== value
    );

    return (
        <div className={"flex flex-col gap-2"}>
            <DebouncedTextField
                key={remountKey}
                value={value ?? ""}
                label={property.name}
                disabled={disabled ?? false}
                error={Boolean(showError && error)}
                onChange={(event) => setValue(event.target.value)}/>

            <Typography variant={"caption"} className={"opacity-70"}>
                Suggested from the words this guide already uses, not from search data.
                Confirm it or replace it.
            </Typography>

            {!disabled && alternatives.length > 0 && (
                <div className={"flex flex-row gap-2 flex-wrap items-center"}>
                    <Typography variant={"caption"} className={"opacity-60"}>Also:</Typography>
                    {alternatives.map((candidate) => (
                        <Chip key={candidate} size={"small"} onClick={() => write(candidate)}>
                            {candidate}
                        </Chip>
                    ))}
                </div>
            )}

            <DerivationNote
                state={state}
                disabled={disabled}
                onReset={() => write((state.derived as string) ?? "")}/>
            <FieldHelperText error={error}
                             showError={showError ?? false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
        </div>
    );
}

/** Secondary keywords as removable chips, with the derived set one click away. */
export function SecondaryKeywordsField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    includeDescription,
    context
}: FieldProps<string[], DerivedFieldProps>) {
    const state = useDerivedState("secondaryKeywords", context);
    const keywords = value ?? [];

    return (
        <div className={"flex flex-col gap-2"}>
            <Typography variant={"label"}>{property.name}</Typography>
            <div className={"flex flex-row gap-2 flex-wrap items-center"}>
                {keywords.length === 0 && (
                    <Typography variant={"caption"} className={"opacity-60"}>None.</Typography>
                )}
                {keywords.map((keyword) => (
                    <Chip
                        key={keyword}
                        size={"small"}
                        onClick={disabled
                            ? undefined
                            : () => setValue(keywords.filter((other) => other !== keyword))}>
                        {keyword} {!disabled && <span className={"opacity-50 ml-1"}>×</span>}
                    </Chip>
                ))}
            </div>
            <Typography variant={"caption"} className={"opacity-70"}>
                Suggested from the guide's own wording. Click one to remove it.
            </Typography>
            <DerivationNote
                state={state}
                disabled={disabled}
                onReset={() => setValue((state.derived as string[]) ?? [])}/>
            <FieldHelperText error={error}
                             showError={showError ?? false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
        </div>
    );
}
